import { isNil } from 'lodash-es';

export const setFitScale = (
  targetContainer?: HTMLDivElement,
  currentClientWidth?: number,
  currentClientHeight?: number,
  isFullScreen?: boolean
) => {
  if (!targetContainer || !currentClientWidth || !currentClientHeight) return;
  const designWidth = 1920;

  let scale = currentClientWidth / designWidth;

  // 实际容器的高度(减去页面顶部导航栏的高度)
  let containerHeight = isFullScreen
    ? document.documentElement.clientHeight
    : document.documentElement.clientHeight - 50;

  /**
   * 1. 当前容器的宽度>=设计稿的宽度时，才进行缩放
   * 2. 先等比缩放, 只考虑宽度, 高度未撑满的话, 取容器高度, 然后再进行flex布局
   *
   */

  if (currentClientWidth >= designWidth) {
    // let currentH = containerHeight >= designHeight ? containerHeight : designHeight;
    let currentH = containerHeight;

    targetContainer.style.width = `${designWidth}px`;

    targetContainer.style.height = `${currentH / scale}px`;
    targetContainer.style.transform = `scale3d(${scale}, ${scale}, ${scale})`;
    targetContainer.style.transformOrigin = 'top left';

    return;
  }
  targetContainer.style.width = '';
  targetContainer.style.height = '';
  targetContainer.style.transform = '';
  targetContainer.style.transformOrigin = '';
};

export function goFullscreen(element: HTMLDivElement) {
  if (document.fullscreenElement) {
    return Promise.reject(new Error('全屏模式被禁用'));
  }
  let result = null;
  if (element.requestFullscreen) {
    result = element.requestFullscreen();
  }
  return result || Promise.reject(new Error('不支持全屏'));
}
export function cancelFullscreen() {
  document.exitFullscreen?.();
}

export const keepTwoNull = (data?: number) => {
  if (!isNil(data)) {
    return Number(data.toFixed(2));
  } else {
    return null;
  }
};

export const keepPercentTwoNull = (data?: number): number | null => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(2));
  } else {
    return null;
  }
};
export const keepPercentTwoLine = (data?: number): number | string => {
  if (!isNil(data)) {
    return Number((data * 100).toFixed(2));
  } else {
    return '--';
  }
};

export const keepTwoOrLine = (data?: number) => {
  if (!isNil(data)) {
    return Number(data.toFixed(2));
  }
  return '--';
};

// 取绝对值
export const keepTwoAbsOrLine = (data?: number) => {
  if (!isNil(data)) {
    return Math.abs(Number(data.toFixed(2)));
  }
  return '--';
};

// 判断所有元素是否为null
export const judgeAllNull = (arr: any[]) => {
  return arr.every(element => element === null);
};

// 判断大于0小于0
export const judgeOverZero = (num?: number) => {
  if (!isNil(num)) {
    return num > 0;
  } else {
    return true;
  }
};

// 保留两位小数并取绝对值
export const keepTwoOrLineAbs = (data?: number) => {
  if (!isNil(data)) {
    return Number(Math.abs(data).toFixed(2));
  }
  return '--';
};

type PowerUnit = 'kWh' | 'MWh';

interface FormatPowerResult {
  value: number | string;
  unit?: PowerUnit | string;
}

/**
 * 将电量单位从 kWh 转换为适当的单位（kWh 或 MWh）
 * @param input 电量（单位为 kWh）
 * @returns 格式化后的值和单位
 */
export function formatPower(input?: number): FormatPowerResult {
  if (typeof input !== 'number' || isNaN(input)) {
    return {
      value: '-',
      unit: '-',
    };
  }
  if (input >= 1000) {
    return {
      value: parseFloat((input / 1000).toFixed(2)), // 保留两位小数
      unit: 'MWh',
    };
  } else {
    return {
      value: parseFloat(input.toFixed(2)),
      unit: 'kWh',
    };
  }
}

/**
 * 将电量单位从 kgce 转换为适当的单位（kgce 或 tce）
 * @param input 碳排总览（单位为 kgce）
 * @returns 格式化后的值和单位
 */
export function formatCarbonEmissions(input?: number): FormatPowerResult {
  if (typeof input !== 'number' || isNaN(input)) {
    return {
      value: '-',
      unit: '-',
    };
  }
  if (input >= 1000) {
    return {
      value: parseFloat((input / 1000).toFixed(2)), // 保留两位小数
      unit: 'tce',
    };
  } else {
    return {
      value: parseFloat(input.toFixed(2)),
      unit: 'kgce',
    };
  }
}

/**
 * 将 kWh 转换为适当单位（kWh 或 MWh），并返回格式化后的字符串
 * @param value 电量数值，单位为 kWh
 * @returns 格式化后的字符串（如 "850 kWh" 或 "1.25 MWh"）
 */
export function formatElectricity(value?: number): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return '--';
  }

  if (value >= 1000) {
    const mw = (value / 1000).toFixed(2);
    return `${mw}MWh`;
  } else {
    return `${value.toFixed(2)}kWh`;
  }
}

type CarbonUnit = 'kgCO2e' | 'tCO2e';

interface FormatCarbonResult {
  value: number | string;
  unit?: CarbonUnit | string;
}

/**
 * 将减排单位从 kgCO2e 转换为适当的单位（kgCO2e 或 tCO2e）
 * @param input 减排（单位为 kgCO2e）
 * @returns 格式化后的值和单位
 */
export function formatCarbon(input?: number): FormatCarbonResult {
  if (typeof input !== 'number' || isNaN(input)) {
    return {
      value: '-',
      unit: '-',
    };
  }
  if (input >= 1000) {
    return {
      value: parseFloat((input / 1000).toFixed(2)), // 保留两位小数
      unit: 'tCO2e',
    };
  } else {
    return {
      value: parseFloat(input.toFixed(2)),
      unit: 'kgCO2e',
    };
  }
}
export function formatCarbonTotCO2(input?: number): FormatCarbonResult {
  if (typeof input !== 'number' || isNaN(input)) {
    return {
      value: '-',
      unit: '-',
    };
  }
  // 计算结果：input / 1000 * 0.5703
  const result = (input / 1000) * 0.5703;

  if (result < 1) {
    return {
      value: parseFloat((result * 1000).toFixed(2)), // 转为 kg
      unit: 'kgCO2e',
    };
  }

  return {
    value: parseFloat(result.toFixed(2)),
    unit: 'tCO2e',
  };
}
